
import React, { useRef, useEffect, useState } from 'react';
import type { RefObject } from 'react';
import { Play, Pause, Trash2, FileText, Music, Plus } from 'lucide-react';
import { useTheme } from '../../zustand/ThemeStore';

type UploadedFile = {
  id: number;
  name: string;
  size: string;
  type: 'audio' | 'text';
  duration?: number;
  file: File;
  url?: string;
};

interface FileListProps {
  uploadedFiles: UploadedFile[];
  playingId: number | null;
  setPlayingId: (id: number | null) => void;
  setUploadedFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  formatTime: (seconds: number) => string;
  fileInputRef: RefObject<HTMLInputElement | null>;
  canUpload: boolean;
  handleFiles: (files: FileList | null) => void;
}

const FileList: React.FC<FileListProps> = ({
  uploadedFiles,
  playingId,
  setPlayingId,
  setUploadedFiles,
  formatTime,
  fileInputRef,
  canUpload,
  handleFiles
}) => {
  const isDarkMode = useTheme((set) => set.theme);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentTime, setCurrentTime] = useState<{ [key: number]: number }>({});
  const [durations, setDurations] = useState<{ [key: number]: number }>({});
  const [isLoading, setIsLoading] = useState<{ [key: number]: boolean }>({});
  const [isDragging, setIsDragging] = useState<boolean>(false);

  // Create object URLs for audio files
  useEffect(() => {
    const newFiles = uploadedFiles.map(file => {
      if (file.type === 'audio' && !file.url && file.file) {
        return { ...file, url: URL.createObjectURL(file.file) };
      }
      return file;
    });
    if (JSON.stringify(newFiles) !== JSON.stringify(uploadedFiles)) {
      setUploadedFiles(newFiles);
    }
    // Cleanup URLs on unmount
    return () => {
      uploadedFiles.forEach(file => {
        if (file.url) URL.revokeObjectURL(file.url);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedFiles.length]);

  const handlePlay = (fileId: number) => {
    const file = uploadedFiles.find(f => f.id === fileId);
    if (!file || file.type !== 'audio') return;
    if (playingId === fileId) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setPlayingId(null);
    } else {
      if (audioRef.current && playingId !== null) {
        audioRef.current.pause();
      }
      setPlayingId(fileId);
      setIsLoading(prev => ({ ...prev, [fileId]: true }));
    }
  };

  useEffect(() => {
    if (playingId !== null) {
      const file = uploadedFiles.find(f => f.id === playingId);
      if (file && file.url && audioRef.current) {
        audioRef.current.src = file.url;
        audioRef.current.play().catch(() => {
          setPlayingId(null);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playingId]);

  const handleTimeUpdate = () => {
    if (audioRef.current && playingId !== null) {
      const current = audioRef.current.currentTime ?? 0;
      setCurrentTime(prev => ({
        ...prev,
        [playingId]: current
      }));
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current && playingId !== null) {
      const duration = audioRef.current.duration ?? 0;
      setDurations(prev => ({
        ...prev,
        [playingId]: duration
      }));
      setIsLoading(prev => ({ ...prev, [playingId]: false }));
    }
  };

  const handleEnded = () => {
    setPlayingId(null);
    if (playingId !== null) {
      setCurrentTime(prev => ({ ...prev, [playingId]: 0 }));
    }
  };

  const handleSeek = (fileId: number, e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const duration = durations[fileId] || 0;
    const newTime = duration * percentage;
    setCurrentTime(prev => ({ ...prev, [fileId]: newTime }));
    if (audioRef.current && playingId === fileId) {
      audioRef.current.currentTime = newTime;
    }
  };

  return (
    <div 
      className={`flex-1 flex flex-col min-h-0 overflow-hidden justify-between p-4 relative transition-all ${
        isDarkMode ? 'bg-[#0a0f0f]' : 'bg-white'
      } ${isDragging && canUpload ? 'border-2 border-dashed border-[#1a9f6b] bg-[#1a9f6b]/5' : ''}`}
      onDragEnter={(e) => {
        if (canUpload) {
          e.preventDefault();
          setIsDragging(true);
        }
      }}
      onDragOver={(e) => {
        if (canUpload) {
          e.preventDefault();
        }
      }}
      onDragLeave={(e) => {
        if (canUpload) {
          // Check if we're really leaving the container
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX;
          const y = e.clientY;
          
          if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
            setIsDragging(false);
          }
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        if (canUpload && e.dataTransfer.files.length > 0) {
          handleFiles(e.dataTransfer.files);
        }
      }}
    >
      {/* Drag overlay */}
      {isDragging && canUpload && (
        <div className="absolute inset-0 z-50 bg-[#1a9f6b]/20 border-2 border-dashed border-[#1a9f6b] rounded-lg flex items-center justify-center backdrop-blur-sm">
          <div className="text-center bg-white/90 dark:bg-black/90 p-6 rounded-lg shadow-lg">
            <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-[#1a9f6b]/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-[#1a9f6b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <p className={`font-semibold text-lg mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Drop files to add more
            </p>
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Audio: MP3, WAV, FLAC, M4A<br />
              Text: TXT, SRT, VTT
            </p>
          </div>
        </div>
      )}
      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        className="hidden"
      />
      
      {/* Hidden File Input */}
      {/* File input is handled by parent, so no need to render here */}
      
      <div 
        className={`h-[28rem] overflow-y-auto pr-2 space-y-3 ${
          isDarkMode ? 'scrollbar-thumb-gray-700' : 'scrollbar-thumb-gray-300'
        }`}
      >
        {uploadedFiles.map((file) => (
          <div key={file.id} className={`rounded-lg p-4 ${
            isDarkMode ? 'bg-[#050a0a]' : 'bg-gray-100'
          }`}>
            <div className="flex items-center gap-3">
              {file.type === 'audio' ? (
                <button
                  onClick={() => handlePlay(file.id)}
                  disabled={isLoading[file.id]}
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#1a9f6b] hover:bg-[#148856] text-white transition-colors disabled:opacity-50"
                >
                  {isLoading[file.id] ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : playingId === file.id ? (
                    <Pause className="w-4 h-4" />
                  ) : (
                    <Play className="w-4 h-4 ml-0.5" />
                  )}
                </button>
              ) : (
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  isDarkMode 
                    ? 'bg-blue-900/30 text-blue-400' 
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  <FileText className="w-4 h-4" />
                </div>
              )}
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className={`text-sm font-medium truncate ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {file.name}
                  </p>
                  {file.type === 'audio' ? (
                    <Music className="w-3 h-3 flex-shrink-0 text-[#1a9f6b]" />
                  ) : (
                    <FileText className={`w-3 h-3 flex-shrink-0 ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                  )}
                </div>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {file.size}
                </p>
              </div>
              
              <button
                onClick={() => {
                  if (playingId === file.id) {
                    audioRef.current?.pause();
                    setPlayingId(null);
                  }
                  setUploadedFiles(prev => prev.filter(f => f.id !== file.id));
                }}
                className={`p-2 rounded-lg transition-all ${
                  isDarkMode 
                    ? 'hover:bg-[#0f1919] text-gray-400 hover:text-red-400' 
                    : 'hover:bg-gray-200 text-gray-400 hover:text-red-500'
                }`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            
            {/* Audio Progress Bar */}
            {file.type === 'audio' && (
              <div className="mt-3 space-y-1">
                <div 
                  className={`h-2 rounded-full cursor-pointer relative overflow-hidden ${
                    isDarkMode ? 'bg-gray-700' : 'bg-gray-300'
                  }`}
                  onClick={(e) => handleSeek(file.id, e)}
                >
                  <div 
                    className="h-full bg-[#1a9f6b] rounded-full transition-all"
                    style={{ 
                      width: `${durations[file.id] ? ((currentTime[file.id] || 0) / durations[file.id]) * 100 : 0}%` 
                    }}
                  />
                  {/* {playingId === file.id && (
                    <div 
                      className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-md"
                      style={{ 
                        left: `${durations[file.id] ? ((currentTime[file.id] || 0) / durations[file.id]) * 100 : 0}%`,
                        transform: 'translate(-50%, -50%)'
                      }}
                    />
                  )} */}
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                    {formatTime(currentTime[file.id] || 0)}
                  </span>
                  <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                    {formatTime(durations[file.id] || file.duration || 0)}
                  </span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      
      {/* Add More Files Button */}
      <div className="pt-4 flex-shrink-0 space-y-2">
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={!canUpload}
          className={`w-full py-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
            canUpload
              ? `${isDarkMode ? 'bg-[#050a0a] hover:bg-[#0f1919] text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`
              : `${isDarkMode ? 'bg-[#050a0a] text-gray-600' : 'bg-gray-100 text-gray-400'} cursor-not-allowed`
          }`}
        >
          <Plus className={`w-4 h-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`} />
          Add More Files
        </button>
        {canUpload && (
          <p className={`text-xs text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Or drag and drop files anywhere above
          </p>
        )}
      </div>
    </div>
  );
};

export default FileList;